import * as React from 'react';

import { Td, Tr } from '@lightspeed/cirrus-table';
import { TextLink } from '@lightspeed/cirrus-text';
import { Button } from '@lightspeed/flame/Button';
import { theme } from '@lightspeed/flame/Core/themes/flame';
import { IconDelete } from '@lightspeed/flame/Icon/Delete';

import Format from 'helpers/format';
import { AlertMessageDTO } from 'root/apps/components/AlertMessageDisplay/AlertMessageDisplay';
import { RowEditInputNumber } from 'root/apps/PurchaseOrder/NuorderOrdering/components/subcomponents/RowEditInputNumber';
import { RowEditInputString } from 'root/apps/PurchaseOrder/NuorderOrdering/components/subcomponents/RowEditInputString';
import { RowNuorderCell } from 'root/apps/PurchaseOrder/NuorderOrdering/components/subcomponents/RowNuorderCell';
import { RowPopover } from 'root/apps/PurchaseOrder/NuorderOrdering/components/subcomponents/RowPopover';
import {
  LOG_SOURCE,
  NuorderOrderingActionTypes,
  useNuorderOrdering,
} from 'root/apps/PurchaseOrder/NuorderOrdering/context/Context';
import {
  useDeletePurchaseLine,
  useUpdateItem,
  useUpdateItemVendorNum,
  useUpdatePurchaseLine,
} from 'root/apps/PurchaseOrder/NuorderOrdering/context/Queries';
import { RowDataSortable } from 'root/apps/PurchaseOrder/NuorderOrdering/types';
import { logError } from 'root/datadog/datadogLogs';

interface OrderLineRowProps {
  data: RowDataSortable;
  isReadOnly?: boolean;
}

export const OrderLineRow: React.FC<OrderLineRowProps> = ({ data, isReadOnly = false }) => {
  const { dispatch } = useNuorderOrdering();
  const [rowAlertMessage, setRowAlertMessage] = React.useState<AlertMessageDTO | null>(null);

  const mutationUpdateItem = useUpdateItem(data.itemID);
  const mutationUpdateItemVendorNum = useUpdateItemVendorNum(data.itemID, data.vendorID);
  const mutationUpdatePurchaseLine = useUpdatePurchaseLine(data.purchaseID, data.id);
  const mutationDeletePurchaseLine = useDeletePurchaseLine(data.purchaseID, data.id);

  /**
   * Handle error in mutation
   */
  React.useEffect(() => {
    if (mutationDeletePurchaseLine.error) {
      dispatch({
        type: NuorderOrderingActionTypes.SET_ALERT_MESSAGE,
        payload: {
          type: 'danger',
          title: 'Error removing item',
          message: 'An error occurred while trying to remove the item. Please try again later.',
        },
      });
      logError(mutationDeletePurchaseLine.error, {
        source: LOG_SOURCE,
        file: 'OrderLineRow.tsx',
      });
    }
  }, [dispatch, mutationDeletePurchaseLine.error]);

  const handleLinkClick = (e: React.MouseEvent) => {
    if (!e.metaKey) {
      e.preventDefault();
      window.navigateToContent('view', 'item.views.item', data.itemID);
    }
  };

  const handleDeleteClick = () => {
    mutationDeletePurchaseLine.mutate();
  };

  const trStyle = React.useMemo(() => {
    if (rowAlertMessage?.type === 'danger') {
      return {
        borderLeft: `5px solid ${theme.colors['maple-500']}`,
        borderRight: `5px solid ${theme.colors['maple-500']}`,
        backgroundColor: `${theme.colors['maple-100']}`,
      };
    }
    if (rowAlertMessage?.type === 'warning') {
      return {
        borderLeft: `5px solid ${theme.colors['orange-500']}`,
        borderRight: `5px solid ${theme.colors['orange-500']}`,
        backgroundColor: `${theme.colors['orange-100']}`,
      };
    }
    return {
      borderLight: `5px solid transparent`,
      borderRight: `5px solid transparent`,
    };
  }, [rowAlertMessage]);

  return (
    <Tr key={data.id} data-test="orderLineRow" style={trStyle}>
      {!isReadOnly && (
        <Td pr={0} data-test="orderLineRow-alertMessage">
          <RowPopover alertMessage={rowAlertMessage} />
        </Td>
      )}
      <Td className="notranslate" data-test="orderLineRow-upc">
        {!isReadOnly && window.merchantos.session.hasRight('product_edit') ? (
          <RowEditInputString
            value={data.upc}
            fieldName="upc"
            mutationUpdate={mutationUpdateItem}
            width={140}
          />
        ) : (
          data.upc
        )}
      </Td>
      <Td className="notranslate" data-test="orderLineRow-description">
        <TextLink
          href={`/?name=item.views.item&form_name=view&id=${data.itemID}`}
          onClick={handleLinkClick}
          data-test="listing-description-link"
        >
          {data.description}
        </TextLink>
      </Td>
      <Td className="notranslate" data-test="orderLineRow-itemVendorNum">
        {!isReadOnly && window.merchantos.session.hasRight('product_edit') ? (
          <RowEditInputString
            value={data.itemVendorNum}
            fieldName="value"
            mutationUpdate={mutationUpdateItemVendorNum}
            panDetection={true}
          />
        ) : (
          data.itemVendorNum
        )}
      </Td>
      {!isReadOnly && (
        <Td className="notranslate" data-test="orderLineRow-availability">
          <RowNuorderCell data={data} setRowAlertMessage={setRowAlertMessage} />
        </Td>
      )}
      <Td className="notranslate" data-test="orderLineRow-qty">
        {!isReadOnly ? (
          <RowEditInputNumber
            value={data.qty}
            fieldName="quantity"
            mutationUpdate={mutationUpdatePurchaseLine}
            min={1}
            roundingMethod={(value) => Math.round(value)}
            width={70}
          />
        ) : (
          data.qty
        )}
      </Td>
      <Td className="notranslate" style={{ textAlign: 'right' }} data-test="orderLineRow-total">
        {Format.toMoney(data.total)}
      </Td>
      <Td style={{ textAlign: 'right' }}>
        {!isReadOnly && (
          <Button
            size="small"
            data-test="deleteButton"
            onClick={handleDeleteClick}
            loading={mutationDeletePurchaseLine.isLoading}
          >
            <IconDelete />
          </Button>
        )}
      </Td>
    </Tr>
  );
};
